import {useCallback, useEffect, useRef, useState} from "react";
import {DayPicker} from "react-day-picker";
import PropTypes from "prop-types";
import moment from "moment";
import classNames from "classnames";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar} from "@fortawesome/free-solid-svg-icons";
import "react-day-picker/dist/style.css";
import SelectDropdown from "./Dropdowns/SelectDropdown";

const parseDate = (v) => (v ? moment(v, "YYYY-MM-DD").toDate() : undefined);
const format = (d) => (d ? moment(d).format("YYYY-MM-DD") : null);
const formatDisplay = (d) => (d ? moment(d).format("MM/DD/YYYY") : "");

const useClickOutside = (ref, handler, active) => {
    useEffect(() => {
        if (!active) return;
        const listener = (e) => {
            if (ref.current && !ref.current.contains(e.target)) handler();
        };
        document.addEventListener("mousedown", listener);
        return () => document.removeEventListener("mousedown", listener);
    }, [ref, handler, active]);
};

const useDropdownPosition = (isOpen, inputRef, numberOfMonths) => {
    const [position, setPosition] = useState("below");
    useEffect(() => {
        if (!isOpen || !inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = numberOfMonths === 2 ? 350 : 320;
        setPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "above" : "below");
    }, [isOpen, inputRef, numberOfMonths]);
    return position;
};

function DateRangeInput(props) {
    const {
        startDate = null,
        endDate = null,
        minDate = null,
        maxDate = null,
        isRequired = false,
        disabled = false,
        onChange = null,
        onClose = null,
        numberOfMonths = 1,
        allowManualInput = true,
        showClearButton = true,
        className = "",
        showMonthYearDropdown = true,
    } = props;

    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [month, setMonth] = useState(new Date());
    const [range, setRange] = useState({from: undefined, to: undefined});
    const [fromValue, setFromValue] = useState("");
    const [toValue, setToValue] = useState("");
    const [open, setOpen] = useState(false);
    const [errors, setErrors] = useState({from: "", to: ""});

    useEffect(() => {
        const from = parseDate(startDate);
        const to = parseDate(endDate);

        if (
            (!range.from && from) ||
            (range.from && from && range.from.getTime() !== from.getTime()) ||
            (!range.to && to) ||
            (range.to && to && range.to.getTime() !== to.getTime())
        ) {
            setRange({from, to});
            setFromValue(startDate || "");
            setToValue(endDate || "");
            if (from) setMonth(from);
        }
    }, [startDate, endDate]);

    const handleClickOutside = useCallback(() => setOpen(false), []);
    useClickOutside(dropdownRef, handleClickOutside, open);
    const dropdownPosition = useDropdownPosition(open, inputRef, numberOfMonths);

    const toggleDropdown = () => {
        if (disabled) return;
        setOpen((v) => !v);
    };

    const validate = (fromVal, toVal) => {
        const errs = {from: "", to: ""};
        const fromDateParsed = moment(fromVal, "YYYY-MM-DD", true);
        const toDateParsed = moment(toVal, "YYYY-MM-DD", true);
        const minDateParsed = minDate ? parseDate(minDate) : null;
        const maxDateParsed = maxDate ? parseDate(maxDate) : null;

        if (fromDateParsed.isValid() && toDateParsed.isValid() && fromDateParsed.isAfter(toDateParsed)) {
            errs.from = "Start date cannot be after end date";
            errs.to = "End date cannot be before start date";
        }
        if (fromDateParsed.isValid() && minDateParsed && fromDateParsed.toDate() < minDateParsed)
            errs.from = "Date before min date";
        if (fromDateParsed.isValid() && maxDateParsed && fromDateParsed.toDate() > maxDateParsed)
            errs.from = "Date after max date";
        if (toDateParsed.isValid() && minDateParsed && toDateParsed.toDate() < minDateParsed)
            errs.to = "Date before min date";
        if (toDateParsed.isValid() && maxDateParsed && toDateParsed.toDate() > maxDateParsed)
            errs.to = "Date after max date";

        setErrors(errs);
        return Object.values(errs).every((v) => v === "");
    };

    const emitChange = useCallback(
        (from, to) => {
            onChange?.({from, to, fromFormatted: format(from), toFormatted: format(to)});
        },
        [onChange]
    );

    const handleDaySelect = (newRange, selectedDay) => {
        if ((range.from && range.to) || (!newRange.from && selectedDay)) {
            const date = selectedDay ? new Date(selectedDay) : undefined;
            setRange({from: date, to: undefined});
            setMonth(date || new Date());
            setFromValue(date ? format(date) : "");
            setToValue("");
            emitChange(date, null);
            return;
        }

        const r = {
            from: newRange?.from ? new Date(newRange.from) : undefined,
            to: newRange?.to ? new Date(newRange.to) : undefined,
        };

        setRange(r);
        if (r.from) setFromValue(format(r.from));
        setToValue(r.to ? format(r.to) : "");
        emitChange(r.from, r.to);
    };

    const clampToBounds = (date) => {
        const minDateParsed = minDate ? parseDate(minDate) : null;
        const maxDateParsed = maxDate ? parseDate(maxDate) : null;

        if (minDateParsed && date < minDateParsed) return minDateParsed;
        if (maxDateParsed && date > maxDateParsed) return maxDateParsed;
        return date;
    };

    const handleInput = (val, type) => {
        const parsed = moment(val, "YYYY-MM-DD", true);
        const clampedValue = parsed.isValid() ? format(clampToBounds(parsed.toDate())) : val;
        const clampedDate = parsed.isValid() ? parseDate(clampedValue) : undefined;

        if (type === "from") {
            setFromValue(clampedValue);
            if (clampedDate) {
                setRange((prev) => ({...prev, from: clampedDate}));
                emitChange(clampedDate, range.to);
            }
        } else {
            setToValue(clampedValue);
            if (clampedDate) {
                setRange((prev) => ({...prev, to: clampedDate}));
                emitChange(range.from, clampedDate);
            }
        }

        validate(type === "from" ? clampedValue : fromValue, type === "to" ? clampedValue : toValue);
    };

    const handleSubmit = () => {
        if (validate(fromValue, toValue)) {
            const from = moment(fromValue, "YYYY-MM-DD").toDate();
            const to = moment(toValue, "YYYY-MM-DD").toDate();
            setRange({from, to});
            emitChange(from, to);
            onClose?.({from, to, fromFormatted: fromValue, toFormatted: toValue});
            setOpen(false);
        }
    };

    const clearDates = () => {
        setRange({from: undefined, to: undefined});
        setFromValue("");
        setToValue("");
        setErrors({from: "", to: ""});
        emitChange(null, null);
    };

    return (
        <div ref={dropdownRef} className={classNames("date-range-input-wrapper", className)}>
            <div ref={inputRef}
                 className={classNames("input-container", {disabled})}
                 onClick={!allowManualInput ? toggleDropdown : undefined}
            >
                {allowManualInput ? (
                    <div className="manual-input-container">
                        <input
                            type="date"
                            value={fromValue}
                            onChange={(e) => handleInput(e.target.value, "from")}
                            disabled={disabled}
                            required={isRequired}
                            min={minDate ? format(parseDate(minDate)) : undefined}
                            max={maxDate ? format(parseDate(maxDate)) : undefined}
                            className={classNames("manual-from-input", {"input-error": errors.from})}
                            data-testid={"start-date"}
                        />
                        <span className="manual-input-separator"> to </span>
                        <input
                            type="date"
                            value={toValue}
                            onChange={(e) => handleInput(e.target.value, "to")}
                            disabled={disabled}
                            required={isRequired}
                            min={minDate ? format(parseDate(minDate)) : undefined}
                            max={maxDate ? format(parseDate(maxDate)) : undefined}
                            className={classNames("manual-to-input", {"input-error": errors.to})}
                            data-testid={"end-date"}
                        />
                    </div>
                ) : (
                    <input
                        value={
                            fromValue && toValue
                                ? `${formatDisplay(fromValue)} - ${formatDisplay(toValue)}`
                                : fromValue
                                    ? `${formatDisplay(fromValue)} - `
                                    : ""
                        }
                        placeholder="Select date range"
                        readOnly
                        required={isRequired}
                        disabled={disabled}
                        className={classNames("single-input", {disabled})}
                    />
                )}

                <FontAwesomeIcon
                    icon={faCalendar}
                    className={classNames("calendar-icon", {disabled})}
                    onClick={toggleDropdown}
                />
            </div>

            {open && !disabled && (
                <div className={classNames("dropdown", dropdownPosition)}>
                    {showMonthYearDropdown && (
                        <div className="month-year-wrapper">
                            <div className="month-year-selector">
                                <SelectDropdown
                                    selectValue={month.getMonth()}
                                    selectLabel={moment().month(month.getMonth()).format("MMMM")}
                                    onChange={(e) => {
                                        const newMonth = new Date(month);
                                        newMonth.setMonth(Number(e.value));
                                        setMonth(newMonth);
                                    }}
                                    options={Array.from({length: 12}, (_, i) => ({
                                        value: i,
                                        label: moment().month(i).format("MMMM"),
                                    }))}
                                />

                                <SelectDropdown
                                    selectValue={month.getFullYear()}
                                    onChange={(e) => {
                                        const newMonth = new Date(month);
                                        newMonth.setFullYear(Number(e.value));
                                        setMonth(newMonth);
                                    }}
                                    options={Array.from({length: 21}, (_, i) => ({
                                        value: new Date().getFullYear() - 10 + i,
                                        label: (new Date().getFullYear() - 10 + i).toString(),
                                    }))}
                                />
                            </div>
                        </div>
                    )}

                    <DayPicker
                        month={month}
                        onMonthChange={setMonth}
                        mode="range"
                        numberOfMonths={numberOfMonths}
                        selected={range}
                        onSelect={handleDaySelect}
                        disabled={[
                            ...(minDate ? [{before: parseDate(minDate)}] : []),
                            ...(maxDate ? [{after: parseDate(maxDate)}] : []),
                        ]}
                    />

                    <div className="footer">
                        <div className="footer-text">
                            {range.from && range.to
                                ? `${moment(range.from).format("MMM D, YYYY")} - ${moment(range.to).format(
                                    "MMM D, YYYY"
                                )}`
                                : range.from
                                    ? `Start: ${moment(range.from).format("MMM D, YYYY")}`
                                    : "Select dates"}
                        </div>

                        {showClearButton && (range.from || range.to) && (
                            <button onClick={clearDates} className="btn btn-clear">
                                Clear
                            </button>
                        )}

                        <button onClick={handleSubmit} className="btn btn-submit">
                            Submit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

DateRangeInput.propTypes = {
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    numberOfMonths: PropTypes.number,
    allowManualInput: PropTypes.bool,
    showClearButton: PropTypes.bool,
    className: PropTypes.string,
    showMonthYearDropdown: PropTypes.bool,
};

export default DateRangeInput;
